// ─────────────────────────────────────────────────────────────────────────────
// LabThree — AppShell (D2 Evoluto)
// Contenitore di navigazione: TopBar + SideBar + main.
// Desktop ≥ 900px : sidebar fissa a sinistra
// Mobile  < 900px : sidebar come overlay, aperta dall'hamburger in TopBar
//
// Puramente presentazionale: la state machine `sezione`/`setSezione` di App.js
// resta invariata. AppShell chiama `onSezione(idVoce)` — stessa firma di
// `setSezione` — e applica `attiva = sezione` per evidenziare la voce.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import LT from './tokens';
import I, { Ico } from './icons';
import { LogoIcon, Card, CatTile } from './components';

// ─────── Helpers di ruolo/piano ──────────────────────────────────────────────

const PIANI_ORDINE = ['START', 'TRACE', 'MASTER'];

function hasPiano(azienda, operatore, pianoMinimo) {
  if (operatore?.master) return true;
  return PIANI_ORDINE.indexOf(azienda?.piano ?? 'START') >= PIANI_ORDINE.indexOf(pianoMinimo);
}

const isMonitoratore = op => op?.monitoratore === true || op?.monitoratore === 1;
const isVerificatore = op => op?.verificatore === true || op?.verificatore === 1;

// ─────── Costruzione voci sidebar ────────────────────────────────────────────
// Ritorna un array piatto di voci. I separatori sono oggetti con `divider`.
// Ogni voce: { id, icon, label, disabled? }
// Gli id coincidono 1:1 con i valori di `sezione` usati da App.js.

export function buildSidebarItems(operatore, azienda) {
  if (!operatore) return [];
  const admin  = !!(operatore.admin || operatore.master);
  const master = !!operatore.master;
  const trace  = hasPiano(azienda, operatore, 'TRACE');

  const items = [];

  // ── Operatore base ────────────────────────────────────────────────────────
  if (!admin) {
    items.push({ id: 'home',        icon: 'home',    label: 'Home' });
    items.push({ id: 'attivita',    icon: 'list',    label: 'Attività',     disabled: !isMonitoratore(operatore) });
    items.push({ id: 'verifiche',   icon: 'shield',  label: 'Verifiche',    disabled: !isVerificatore(operatore) });
    items.push({ id: 'storico',     icon: 'history', label: 'Storico' });
    items.push({ id: 'documenti',   icon: 'folder',  label: 'Documenti' });
    items.push({ id: 'registro-nc', icon: 'alert',   label: 'Non conformità' });
    if (trace) {
      items.push({ divider: true, label: 'Moduli' });
      items.push({ id: 'tracciabilita', icon: 'route', label: 'Tracciabilità' });
    }
    items.push({ divider: true, label: 'Supporto' });
    items.push({ id: 'aiuto', icon: 'info', label: 'Centro assistenza' });
    return items;
  }

  // ── Admin / Master ────────────────────────────────────────────────────────
  items.push({ id: 'dashboard',   icon: 'chart',   label: 'Dashboard' });
  items.push({ id: 'attivita',    icon: 'list',    label: 'Attività' });
  if (isVerificatore(operatore) || master) {
    items.push({ id: 'verifiche', icon: 'shield',  label: 'Verifiche' });
  }
  items.push({ id: 'storico',     icon: 'history', label: 'Storico' });
  items.push({ id: 'registro-nc', icon: 'alert',   label: 'Non conformità' });
  items.push({ id: 'correzioni',  icon: 'edit',    label: 'Correzioni' });
  items.push({ id: 'documenti',   icon: 'folder',  label: 'Documenti' });
  if (trace) {
    items.push({ id: 'tracciabilita', icon: 'route', label: 'Tracciabilità' });
  }

  items.push({ divider: true, label: 'Gestione' });
  items.push({ id: 'mia-azienda',  icon: 'building', label: 'La mia azienda' });
  items.push({ id: 'organico',     icon: 'users',    label: 'Organico' });
  items.push({ id: 'oggetti',      icon: 'package',  label: 'Oggetti' });
  items.push({ id: 'scadenziario', icon: 'calendar', label: 'Scadenziario' });

  if (master) {
    items.push({ divider: true, label: 'Master' });
    items.push({ id: 'ambiti',       icon: 'layers',   label: 'Ambiti e tipi' });
    items.push({ id: 'aziende',      icon: 'building', label: 'Aziende' });
    items.push({ id: 'impostazioni', icon: 'settings', label: 'Impostazioni' });
  }

  items.push({ divider: true, label: 'Supporto' });
  items.push({ id: 'aiuto', icon: 'info', label: 'Centro assistenza' });

  return items;
}

// ─────── Breakpoint listener ─────────────────────────────────────────────────

function useDesktop(breakpoint = 900) {
  const [desktop, setDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : true
  );
  useEffect(() => {
    const onResize = () => setDesktop(window.innerWidth >= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return desktop;
}

// Hook mobile (<640px) esportato per uso nei contenuti. Indipendente da useDesktop
// perché la soglia "mobile" è diversa dalla soglia "sidebar fissa".
export function useIsMobile(breakpoint = 640) {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return mobile;
}

// ─────── SideBar custom (con divider + logout) ───────────────────────────────
// Wrapper attorno a SideBar di components.js per supportare i divider-label e
// un footer con pulsante "Esci". Ulteriori voci passano attraverso invariate.

function AppSideBar({ items, attiva, onSezione, onLogout, onClose, showClose = false, operatore, azienda }) {
  return (
    <div
      style={{
        width: 240,
        background: '#fff',
        borderRight: `1px solid ${LT.n150}`,
        padding: '14px 10px 10px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
      }}
    >
      {showClose && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 6px 10px' }}>
          <LogoIcon size={28} />
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 999,
              background: LT.n100, border: 'none', cursor: 'pointer',
              color: LT.n600, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Chiudi menu"
          >
            <Ico d={I.x} size={16} />
          </button>
        </div>
      )}

      {/* Identità utente */}
      {operatore && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', marginBottom: 10,
            background: LT.n50, borderRadius: LT.r.md,
          }}
        >
          <div
            style={{
              width: 34, height: 34, borderRadius: 999,
              background: LT.grad, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}
          >
            {(operatore.nome?.[0] || '') + (operatore.cognome?.[0] || '')}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: LT.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {operatore.nome} {operatore.cognome}
            </div>
            <div style={{ fontSize: 11, color: LT.n500, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {operatore.master ? 'Master' : (operatore.admin ? 'Amministratore' : 'Operatore')}
              {azienda?.ragione_sociale ? ` · ${azienda.ragione_sociale}` : ''}
            </div>
          </div>
        </div>
      )}

      {/* Voci */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((it, i) => {
          if (it.divider) {
            return (
              <div
                key={`div-${i}`}
                style={{
                  fontSize: 10, fontWeight: 700, color: LT.n400,
                  textTransform: 'uppercase', letterSpacing: '.08em',
                  padding: '14px 12px 6px',
                }}
              >
                {it.label}
              </div>
            );
          }
          const on = it.id === attiva;
          const disabled = !!it.disabled;
          return (
            <div
              key={it.id}
              onClick={() => {
                if (disabled) return;
                onSezione && onSezione(it.id);
                onClose && onClose();
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '9px 12px', borderRadius: LT.r.md,
                fontSize: 13, fontWeight: on ? 600 : 500,
                background: on ? LT.bluSoft : 'transparent',
                color: disabled ? LT.n400 : (on ? LT.blu : LT.n600),
                cursor: disabled ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                opacity: disabled ? 0.7 : 1,
              }}
              title={disabled ? 'Non abilitato per il tuo profilo' : undefined}
            >
              {it.icon && <Ico d={I[it.icon]} size={17} />}
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.badge != null && (
                <span
                  style={{
                    fontSize: 10, fontWeight: 700,
                    padding: '2px 6px', borderRadius: 999,
                    background: on ? LT.blu : LT.n200,
                    color: on ? '#fff' : LT.n600,
                  }}
                >
                  {it.badge}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer: logout */}
      <div style={{ padding: '10px 6px 4px', borderTop: `1px solid ${LT.n150}`, marginTop: 8 }}>
        <button
          onClick={onLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: LT.r.md,
            border: 'none', background: 'transparent',
            fontSize: 13, fontWeight: 500, color: LT.n600,
            cursor: 'pointer', fontFamily: LT.sans,
          }}
        >
          <Ico d={I.logout} size={17} />
          <span>Esci</span>
        </button>
      </div>
    </div>
  );
}

// ─────── TopBar interno (logo azienda per multi-tenant + hamburger mobile) ───

function AppTopBar({ azienda, desktop, onHamburger }) {
  const BACKEND = typeof window !== 'undefined' ? (window.__LT_BACKEND__ || '') : '';
  const logoAzienda = azienda?.logo ? `${BACKEND}${azienda.logo}` : null;

  return (
    <div
      style={{
        height: 60, display: 'flex', alignItems: 'center',
        padding: '0 16px', background: '#fff',
        borderBottom: `1px solid ${LT.n150}`,
        flexShrink: 0, position: 'sticky', top: 0, zIndex: 50,
      }}
    >
      {!desktop && (
        <button
          onClick={onHamburger}
          style={{
            width: 36, height: 36, marginRight: 8,
            borderRadius: LT.r.md, background: LT.n100, border: 'none',
            cursor: 'pointer', color: LT.n700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Apri menu"
        >
          <Ico d={I.list} size={20} />
        </button>
      )}

      {/* Logo azienda (personalizzazione multi-tenant) o fallback LabThree */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {logoAzienda ? (
          <img
            src={logoAzienda}
            alt={azienda?.ragione_sociale || 'Azienda'}
            style={{ maxHeight: 40, maxWidth: 140, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <>
            <LogoIcon size={28} />
            <span style={{ fontSize: 15, fontWeight: 700, color: LT.n800 }}>LabThree</span>
          </>
        )}
        {azienda?.ragione_sociale && logoAzienda && (
          <span
            style={{
              marginLeft: 4, paddingLeft: 10,
              borderLeft: `1px solid ${LT.n150}`,
              fontSize: 12, color: LT.n500, fontWeight: 500,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240,
            }}
          >
            {azienda.ragione_sociale}
          </span>
        )}
      </div>

      <div style={{ flex: 1 }} />

      {/* Brand LabThree (logo orizzontale — identifica il prodotto, non il fornitore) */}
      <img
        src={`${process.env.PUBLIC_URL || ''}/brand/logo-orizzontale.svg`}
        alt="LabThree"
        style={{ height: 28, width: 'auto', display: 'block', flexShrink: 0 }}
      />
    </div>
  );
}

// ─────── AppShell (componente esposto) ───────────────────────────────────────

export function AppShell({
  operatore,
  azienda,
  impostazioni,
  sezione,
  onSezione,
  onLogout,
  children,
}) {
  const desktop = useDesktop();
  const [menuOpen, setMenuOpen] = useState(false);

  // Quando si passa a desktop chiudi il drawer se era aperto.
  useEffect(() => { if (desktop) setMenuOpen(false); }, [desktop]);

  const items = buildSidebarItems(operatore, azienda);

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans, color: LT.n800 }}>
      <AppTopBar
        azienda={azienda}
        desktop={desktop}
        onHamburger={() => setMenuOpen(true)}
      />

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 60px)' }}>
        {/* Sidebar desktop fissa */}
        {desktop && (
          <AppSideBar
            items={items}
            attiva={sezione}
            onSezione={onSezione}
            onLogout={onLogout}
            operatore={operatore}
            azienda={azienda}
          />
        )}

        {/* Sidebar mobile come overlay */}
        {!desktop && menuOpen && (
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
              zIndex: 100, display: 'flex',
            }}
          >
            <div onClick={e => e.stopPropagation()} style={{ height: '100%' }}>
              <AppSideBar
                items={items}
                attiva={sezione}
                onSezione={onSezione}
                onLogout={onLogout}
                operatore={operatore}
                azienda={azienda}
                showClose
                onClose={() => setMenuOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Content area */}
        {/* NIENTE overflow qui dentro.
            Un antenato con overflow diverso da "visible" diventa un contenitore
            di scorrimento, e da quel momento le barre "position: sticky" delle
            schermate si agganciano a LUI invece che alla finestra. Siccome
            questo <main> non scorre mai per conto suo (l'altezza e' un
            minHeight, quindi cresce col contenuto), quelle barre non si
            fermavano mai in cima: scorrevano via insieme alla pagina e il
            pulsante Indietro spariva. Verificato in browser il 19/08/2026:
            con overflow:auto la barra finiva a -800px, senza resta a 0.
            minWidth:0 basta a impedire che il contenuto largo sfondi il flex. */}
        <main style={{ flex: 1, minWidth: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}

// ─────── HomeOperatore (landing asciutta — scelta X) ─────────────────────────
// Saluto + card shortcut alle sezioni principali dell'operatore base.
// Rispetta gating monitoratore/verificatore/TRACE.

function CardShortcut({ cat, titolo, sottotitolo, onClick, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        background: '#fff', borderRadius: LT.r.lg, boxShadow: LT.sh2,
        padding: 18, border: `1px solid ${LT.n150}`,
        display: 'flex', alignItems: 'center', gap: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'transform .12s, box-shadow .12s',
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.boxShadow = LT.sh3; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = LT.sh2; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <CatTile cat={cat} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: LT.n900 }}>{titolo}</div>
        {sottotitolo && (
          <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>{sottotitolo}</div>
        )}
      </div>
      {!disabled && <Ico d={I.chevronR} size={18} style={{ color: LT.n400 }} />}
    </div>
  );
}

export function HomeOperatore({ operatore, azienda, onSezione }) {
  const trace = hasPiano(azienda, operatore, 'TRACE');
  const today = new Date();
  const dataStr = today.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="lt-page" style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      {/* Hero */}
      <Card
        pad={28}
        className="lt-hero"
        style={{
          background: LT.grad, color: '#fff', border: 'none',
          position: 'relative', overflow: 'hidden', marginBottom: 20,
        }}
      >
        <div
          style={{
            position: 'absolute', top: -40, right: -40,
            width: 200, height: 200, borderRadius: 999,
            background: 'rgba(255,255,255,.08)', pointerEvents: 'none',
          }}
        />
        <div style={{ fontSize: 12, fontWeight: 600, opacity: .85, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {dataStr}
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, letterSpacing: '-.02em' }}>
          Ciao {operatore?.nome || ''}
        </div>
        {azienda?.ragione_sociale && (
          <div style={{ fontSize: 13, opacity: .9, marginTop: 4 }}>
            {azienda.ragione_sociale}
          </div>
        )}
      </Card>

      {/* Section label */}
      <div
        style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em',
          margin: '8px 4px 12px',
        }}
      >
        Operatività
      </div>

      {/* Grid shortcuts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 14,
        }}
      >
        <CardShortcut
          cat="temperature"
          titolo="Le mie attività"
          sottotitolo={isMonitoratore(operatore) ? 'Monitoraggi da eseguire' : 'Non abilitato per il tuo profilo'}
          onClick={() => onSezione('attivita')}
          disabled={!isMonitoratore(operatore)}
        />
        <CardShortcut
          cat="produzione"
          titolo="Le mie verifiche"
          sottotitolo={isVerificatore(operatore) ? 'Attività da verificare' : 'Non abilitato per il tuo profilo'}
          onClick={() => onSezione('verifiche')}
          disabled={!isVerificatore(operatore)}
        />
        <CardShortcut
          cat="pulizie"
          titolo="Storico monitoraggi"
          sottotitolo="Registro storico attività"
          onClick={() => onSezione('storico')}
        />
        <CardShortcut
          cat="formazione"
          titolo="Documenti HACCP"
          sottotitolo="Procedure, modulistica, tecnici"
          onClick={() => onSezione('documenti')}
        />
        <CardShortcut
          cat="manutenzioni"
          titolo="Non conformità"
          sottotitolo="Registro NC e azioni correttive"
          onClick={() => onSezione('registro-nc')}
        />
        {trace && (
          <CardShortcut
            cat="tracciabilita"
            titolo="Tracciabilità"
            sottotitolo="Fornitori, ingredienti, prodotti, fasi"
            onClick={() => onSezione('tracciabilita')}
          />
        )}
      </div>
    </div>
  );
}

// Ri-export utility che potrebbero servire dall'esterno
export { hasPiano };
